
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InteractiveService } from '../../services/interactive/InteractiveService';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import 'katex/dist/katex.min.css';
import { renderRichContent } from './renderRichContent';

// Pháo giấy / emoji bay khi gửi câu trả lời — tạo cảm giác hào hứng
const Confetti = () => {
  const emojis = ['🎉', '⭐', '✨', '🎊', '💯', '🔥', '🚀', '👏'];
  const pieces = Array.from({ length: 22 });
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.25;
        const dur = 1.3 + Math.random() * 0.9;
        return (
          <motion.div
            key={i}
            initial={{ top: '-8%', left: `${left}vw`, opacity: 0, rotate: 0 }}
            animate={{ top: '108%', opacity: [0, 1, 1, 0], rotate: 540 }}
            transition={{ duration: dur, delay, ease: 'easeIn' }}
            className="absolute text-3xl"
          >
            {emojis[i % emojis.length]}
          </motion.div>
        );
      })}
    </div>
  );
};

// Hiển thị nội dung có công thức ($...$), ẢNH và BẢNG (markdown) — renderer dùng chung GV/HS.
const renderRich = (s: any): React.ReactNode => renderRichContent(s);
// Note: Assuming framer-motion is installed as it's common in modern react apps. 
// If not, I should use standard CSS transitions. 
// Checked package.json earlier, didn't explicitly see it but 'ui' chunk had it?
// Let's stick to standard CSS Tailwind classes to be safe and fast.

const StudentClient = () => {
    const { code: urlCode } = useParams();

    const [step, setStep] = useState<'join' | 'waiting' | 'answering'>('join');
    const [name, setName] = useState('');
    const [code, setCode] = useState(urlCode || '');
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [question, setQuestion] = useState<any>(null);
    const [submitted, setSubmitted] = useState<any>(null);
    const [selected, setSelected] = useState<string[]>([]);   // MCQ nhiều đáp án
    const [locked, setLocked] = useState(false);              // GV đã khoá câu hỏi
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
    const [isMuted, setIsMuted] = useState(false);            // bật/tắt nhạc
    const [confetti, setConfetti] = useState(false);          // pháo giấy khi gửi
    const bgmRef = useRef<HTMLAudioElement | null>(null);
    const sfxRef = useRef<HTMLAudioElement | null>(null);

    // Khởi tạo audio
    useEffect(() => {
        bgmRef.current = new Audio('/sounds/game-background.mp3');
        bgmRef.current.loop = true;
        bgmRef.current.volume = 0.25;
        sfxRef.current = new Audio('/sounds/mixkit-correct-answer-fast-notification-953.wav');
        sfxRef.current.volume = 0.6;
        return () => { bgmRef.current?.pause(); };
    }, []);

    // Phát/tắt nhạc nền theo trạng thái + chế độ mute
    useEffect(() => {
        const bgm = bgmRef.current;
        if (!bgm) return;
        if (isMuted || step === 'join') bgm.pause();
        else bgm.play().catch(() => { });
    }, [isMuted, step]);

    // Nút bật/tắt nhạc (nổi góc phải trên, hiện ở mọi màn hình)
    const MuteButton = () => (
        <button
            onClick={() => setIsMuted((m) => !m)}
            title={isMuted ? 'Bật nhạc' : 'Tắt nhạc'}
            className={`fixed top-4 right-4 z-[60] p-3 rounded-full shadow-lg backdrop-blur-md transition-all active:scale-90 ${isMuted ? 'bg-white/20 text-white/70' : 'bg-white text-indigo-600'}`}
        >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
    );

    const handleJoin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !code) return;

        setConnectionStatus('connecting');

        // Check if code valid
        const isValid = await InteractiveService.checkSession(code);
        if (!isValid) {
            alert('Mã phiên không hợp lệ!');
            setConnectionStatus('error');
            return;
        }

        const socket = InteractiveService.connectStudent(code, name, (newQuestion) => {
            if (newQuestion) {
                setQuestion(newQuestion);
                setStep('answering');
                setSubmitted(null);
                setSelected([]);
                setLocked(false);
            } else {
                setStep('waiting');
                setQuestion(null);
            }
        }, () => setLocked(true));

        socket.onopen = () => setConnectionStatus('connected');
        socket.onerror = () => {
            alert('Lỗi kết nối!');
            setConnectionStatus('error');
        };

        setWs(socket);
        setStep('waiting');
    };

    const submitAnswer = (answer: any) => {
        if (!ws) return;
        ws.send(JSON.stringify({
            action: 'SUBMIT_ANSWER',
            payload: { answer }
        }));
        setSubmitted(answer);
        // Hiệu ứng: âm thanh + pháo giấy
        if (!isMuted && sfxRef.current) {
            try { sfxRef.current.currentTime = 0; sfxRef.current.play().catch(() => { }); } catch { }
        }
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2600);
    };

    // --- Render Screens ---

    if (step === 'join') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex flex-col items-center justify-center p-6 text-white font-sans relative overflow-hidden">
                {/* Bong bóng nền chuyển động */}
                {[0, 1, 2, 3].map((i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-white/10 blur-2xl"
                        style={{ width: 120 + i * 60, height: 120 + i * 60, left: `${15 + i * 22}%`, top: `${10 + i * 18}%` }}
                        animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
                        transition={{ duration: 6 + i * 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                ))}
                <MuteButton />
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 18 }}
                    className="w-full max-w-sm bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl shadow-2xl relative z-10">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-white rounded-2xl mx-auto flex items-center justify-center text-3xl shadow-lg mb-4">
                            🎓
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight">Tham gia lớp học</h1>
                        <p className="text-white/60 text-sm mt-1">Vật Lí 365 Interactive</p>
                    </div>

                    <form onSubmit={handleJoin} className="flex flex-col gap-5">
                        <div className="relative group">
                            <label className="text-xs font-bold uppercase tracking-wider text-indigo-200 mb-1 block group-focus-within:text-white transition-colors">Mã PIN</label>
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center text-3xl font-mono tracking-[0.2em] font-bold outline-none focus:bg-white/10 focus:border-white/30 transition-all placeholder-white/20 text-white"
                                placeholder="000000"
                                maxLength={6}
                                inputMode="numeric"
                            />
                        </div>

                        <div className="relative group">
                            <label className="text-xs font-bold uppercase tracking-wider text-indigo-200 mb-1 block group-focus-within:text-white transition-colors">Họ và Tên</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-lg font-medium outline-none focus:bg-white/10 focus:border-white/30 transition-all placeholder-white/20 text-white"
                                placeholder="Nhập tên của bạn..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!name || !code}
                            className={`mt-4 py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2
                                ${name && code
                                    ? 'bg-white text-indigo-600 hover:bg-indigo-50 hover:shadow-xl'
                                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                                }`}
                        >
                            Vào lớp ngay 🚀
                        </button>
                    </form>
                </motion.div>
                <div className="mt-8 text-xs text-white/30 font-medium tracking-widest uppercase relative z-10">
                    Powered by VatLi365
                </div>
            </div>
        );
    }

    if (step === 'waiting') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
                <MuteButton />
                {/* Emoji vật lí bay lơ lửng */}
                {['⚛️', '🔭', '🧲', '⚡', '🌌', '🔬'].map((e, i) => (
                    <motion.div
                        key={i}
                        className="absolute text-4xl opacity-30"
                        style={{ left: `${10 + i * 15}%`, top: `${15 + (i % 3) * 25}%` }}
                        animate={{ y: [0, -25, 0], rotate: [0, 12, -12, 0] }}
                        transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        {e}
                    </motion.div>
                ))}
                <motion.div
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 180, damping: 16 }}
                    className="w-full max-w-md bg-white/95 backdrop-blur-md p-8 rounded-3xl shadow-2xl border border-white/40 relative z-10"
                >
                    <motion.div
                        className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6 text-5xl"
                        animate={{ scale: [1, 1.12, 1] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        🎯
                    </motion.div>
                    <h2 className="text-3xl font-black text-slate-800 mb-2">Xin chào, {name}! 👋</h2>
                    <p className="text-slate-500">Bạn đã vào lớp thành công.</p>
                    <div className="my-6 py-3 px-6 bg-indigo-50 rounded-xl inline-block font-mono font-bold text-indigo-600 tracking-widest text-xl">
                        {code}
                    </div>
                    <motion.p
                        className="text-sm font-bold text-indigo-400 uppercase tracking-wide"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        Hướng mắt lên màn hình giáo viên ✨
                    </motion.p>
                </motion.div>
            </div>
        );
    }

    // Answering Step
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-100 to-indigo-50 flex flex-col font-sans">
            <MuteButton />
            <AnimatePresence>{confetti && <Confetti />}</AnimatePresence>
            {/* Header */}
            <header className="bg-white px-6 py-4 shadow-sm border-b sticky top-0 z-10">
                <div className="max-w-xl mx-auto flex justify-between items-center">
                    <div className="font-bold text-slate-700 flex items-center gap-2">👤 {name}</div>
                    <div className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-500">{code}</div>
                </div>
            </header>

            <main className="flex-1 flex flex-col max-w-xl mx-auto w-full p-6">
                {/* Question Info */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
                    <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-lg uppercase tracking-wider mb-3">
                        {question?.type === 'mcq' ? 'Trắc nghiệm' : question?.type === 'word_cloud' ? 'Word Cloud' : 'Trả lời ngắn'}
                    </span>
                    <div className="text-xl font-bold text-slate-800 leading-relaxed">
                        {renderRich(question?.data?.text)}
                    </div>
                    {question?.data?.image && (
                        <img src={question.data.image} alt="" className="mt-3 max-h-64 rounded-lg border border-slate-100 mx-auto" />
                    )}
                </div>

                {/* Answer Area */}
                <div className="flex-1">
                    {submitted ? (
                        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                            <motion.div
                                initial={{ scale: 0, rotate: -90 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 14 }}
                                className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-500 shadow-lg"
                            >
                                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </motion.div>
                            <motion.h3 initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }} className="text-2xl font-black text-slate-800 mb-2">Tuyệt vời! Đã gửi 🎉</motion.h3>

                            {question.type === 'mcq' && (
                                <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm w-full">
                                    <p className="text-sm text-slate-400 mb-1 uppercase tracking-wide font-bold">Lựa chọn của bạn</p>
                                    <div className="text-2xl font-black text-indigo-600 flex flex-wrap gap-x-3 gap-y-1 justify-center">
                                        {(Array.isArray(submitted) ? submitted : [submitted]).map((x: any, i: number) => <span key={i}>{renderRich(x)}</span>)}
                                    </div>
                                </div>
                            )}

                            {(question.type === 'short_answer' || question.type === 'word_cloud') && (
                                <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm w-full">
                                    <p className="text-sm text-slate-400 mb-1 uppercase tracking-wide font-bold">Bạn đã nhập</p>
                                    <p className="text-lg font-medium text-slate-800 italic">"{submitted}"</p>
                                </div>
                            )}

                            <p className="mt-8 text-slate-400 animate-pulse">Đang đợi câu hỏi tiếp theo...</p>
                        </div>
                    ) : locked ? (
                        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                            <div className="text-5xl mb-3">🔒</div>
                            <h3 className="text-2xl font-bold text-slate-700">Đã khoá</h3>
                            <p className="text-slate-400 mt-1">Giáo viên đã khoá câu hỏi này. Chờ câu tiếp theo…</p>
                        </div>
                    ) : (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            {question?.type === 'mcq' && (
                                <div>
                                    <p className="text-xs text-slate-400 mb-2">
                                        {question?.data?.multi
                                            ? <>Có thể chọn <b>nhiều ý đúng</b> rồi bấm Gửi.</>
                                            : <>Chọn <b>1 đáp án</b> rồi bấm Gửi.</>}
                                    </p>
                                    <div className="grid grid-cols-1 gap-3">
                                        {(question.data.options || ['A', 'B', 'C', 'D']).map((opt: string, idx: number) => {
                                            const colors = ['border-l-blue-500', 'border-l-green-500', 'border-l-yellow-500', 'border-l-red-500', 'border-l-purple-500'];
                                            const colorClass = colors[idx % colors.length];
                                            const on = selected.includes(opt);
                                            const isMulti = !!question?.data?.multi;
                                            return (
                                                <motion.button
                                                    key={opt}
                                                    initial={{ opacity: 0, x: -30 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.08, type: 'spring', stiffness: 220, damping: 18 }}
                                                    whileTap={{ scale: 0.96 }}
                                                    onClick={() => setSelected((s) => isMulti ? (s.includes(opt) ? s.filter(x => x !== opt) : [...s, opt]) : [opt])}
                                                    className={`w-full p-5 border border-l-8 ${colorClass} rounded-xl shadow-sm hover:shadow-md transition-all text-left ${on ? 'bg-indigo-50 ring-2 ring-indigo-400' : 'bg-white'}`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${on ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                                            {String.fromCharCode(65 + idx)}
                                                        </span>
                                                        <span className="text-lg font-medium text-slate-800">{renderRich(opt)}</span>
                                                        {on && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto text-indigo-600 font-bold text-xl">✓</motion.span>}
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                    <button
                                        onClick={() => { if (selected.length) submitAnswer([...selected]) }}
                                        disabled={selected.length === 0}
                                        className="w-full mt-4 py-4 bg-indigo-600 text-white rounded-xl font-bold text-xl shadow-lg hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Gửi đáp án 🚀 {selected.length > 0 ? `(${selected.length})` : ''}
                                    </button>
                                </div>
                            )}

                            {(question?.type === 'word_cloud' || question?.type === 'short_answer') && (
                                <div>
                                    <textarea
                                        className="w-full p-6 bg-white border-2 border-indigo-100 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none text-xl min-h-[200px] shadow-sm mb-4 placeholder-slate-300 transition-all text-slate-700"
                                        placeholder="Nhập câu trả lời của bạn tại đây..."
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const val = e.currentTarget.value.trim();
                                                if (val) submitAnswer(val);
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={(e) => {
                                            const textarea = e.currentTarget.previousSibling as HTMLTextAreaElement;
                                            const val = textarea.value.trim();
                                            if (val) submitAnswer(val);
                                        }}
                                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-xl shadow-lg hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                                    >
                                        Gửi đi 🚀
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default StudentClient;
